<template>
  <v-card
    :id="`vav_${VAVInfo.deviceName}`"
    class="rounded-2"
    :width="cardWidth"
    elevation="24"
  >
    <!--Card Header-->
    <v-toolbar color="primary" height="88">
      <v-alert variant="text" class="ml-n4">
        <v-container>
          <v-row>
            <v-col class="d-flex">
              <span class="text-h6 font-weight-bold ml-1">
                {{ VAVInfo.friendlyName }}
              </span>
            </v-col>
            <v-col :cols="height >= maxHeight ? 2 : 1">
              <!--Dirty Icon-->
              <v-tooltip location="bottom" content-class="vav-control-tooltip">
                <template v-slot:activator="{ props }">
                  <v-icon
                    class="mt-2 vav-control-dirty-icon"
                    :class="height >= maxHeight ? 'ml-6' : 'ml-15'"
                    color="accentOrange"
                    v-bind="props"
                    v-if="isVAVDirty"
                  >
                    {{ 'mdi-alert-circle' }}
                  </v-icon>
                </template>
                <!--Warnings-->
                <VAVCardAlert
                  :isVAVDirty="isVAVDirty"
                  :getVAVNewSettings="getVAVNewSettings"
                />
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex">
              <span
                class="text-aliceBlue text-caption ml-1 mt-n4 font-weight-bold"
              >
                {{ VAVInfo.siteLocationName }}
              </span>
            </v-col>
          </v-row>
        </v-container>
      </v-alert>
    </v-toolbar>
    <!--Card Body-->
    <v-container class="pa-4 rounded-2">
      <v-row>
        <!--Temperature Card-->
        <v-col>
          <v-card variant="outlined" height="104" class="mb-n5">
            <v-row
              align="center"
              justify="center"
              class="mt-3 d-flex justify-center flex-column"
            >
              <span class="text-primary text-caption font-weight-bold">
                <span>Temperature (ºF)</span>
              </span>
              <span class="text-primary text-h3 font-weight-bold">
                {{ VAVInfo.spaceTemperature }}&deg;
              </span>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <!--Zone Setpoints Card-->
        <v-col :cols="height >= maxHeight ? 12 : 4">
          <v-card variant="outlined" class="mb-n5 px-2">
            <v-row>
              <v-col>
                <span
                  class="text-primary text-caption text-sm-subtitle-1 font-weight-bold float-left pa-2"
                >
                  Zone Setpoints
                </span>
              </v-col>
            </v-row>

            <v-divider></v-divider>

            <v-row class="mt-1 px-1">
              <v-col>
                <v-number-input
                  class="rounded-2 text-primary"
                  :color="userCanUpdate ? 'warning' : 'primary'"
                  :readonly="!userCanUpdate"
                  :base-color="userCanUpdate ? 'warning' : 'primary'"
                  inset
                  control-variant="stacked"
                  :step="0.5"
                  :precision="2"
                  :min="VAVInfo.minOccupiedSetpoint"
                  :max="VAVInfo.maxOccupiedSetpoint"
                  label="Occupied Setpoint (ºF)"
                  v-model="localSettings.occupiedSetpoint"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
            </v-row>
            <v-row class="px-1 mt-n7">
              <v-col>
                <v-number-input
                  class="rounded-2 text-primary"
                  :color="userCanUpdate ? 'warning' : 'primary'"
                  :readonly="!userCanUpdate"
                  :base-color="userCanUpdate ? 'warning' : 'primary'"
                  inset
                  control-variant="stacked"
                  :step="0.5"
                  :precision="2"
                  label="Occupied Setpoint Max (ºF)"
                  v-model="localSettings.maxOccupiedSetpoint"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
            </v-row>
            <v-row class="px-1 mt-n7">
              <v-col>
                <v-number-input
                  class="rounded-2 text-primary"
                  :color="userCanUpdate ? 'warning' : 'primary'"
                  :readonly="!userCanUpdate"
                  :base-color="userCanUpdate ? 'warning' : 'primary'"
                  inset
                  control-variant="stacked"
                  :step="0.5"
                  :precision="2"
                  label="Occupied Setpoint Min (ºF)"
                  v-model="localSettings.minOccupiedSetpoint"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <!--Operating Status Card-->
        <v-col :cols="height >= maxHeight ? 12 : 4">
          <v-card variant="outlined" class="mb-n5 px-2">
            <v-row>
              <v-col>
                <span
                  class="text-primary text-caption text-sm-subtitle-1 font-weight-bold float-left pa-2"
                >
                  Operating Status
                </span>
              </v-col>
            </v-row>

            <v-divider></v-divider>

            <v-row class="mt-1 px-1">
              <v-col>
                <v-text-field
                  class="text-primary"
                  label="Supply Air Temperature (ºF)"
                  :model-value="VAVInfo.supplyAirTemperature"
                  variant="outlined"
                  density="compact"
                  readonly
                />
              </v-col>
            </v-row>
            <v-row class="mt-n7">
              <v-col>
                <span
                  class="text-primary text-caption text-sm-subtitle-1 font-weight-bold float-left pa-2"
                >
                  Current Airflow
                </span>
              </v-col>
            </v-row>

            <v-divider></v-divider>

            <v-row class="mt-1 px-1">
              <v-col cols="6">
                <v-number-input
                  class="rounded-2 text-primary"
                  :color="userCanUpdate ? 'warning' : 'primary'"
                  :readonly="!userCanUpdate"
                  :base-color="userCanUpdate ? 'warning' : 'primary'"
                  inset
                  control-variant="stacked"
                  :step="0.5"
                  :precision="2"
                  :min="0"
                  :max="100"
                  label="Heating Signal %"
                  v-model="localSettings.heatingSignal"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
              <v-col>
                <v-number-input
                  class="rounded-2 text-primary"
                  :color="userCanUpdate ? 'warning' : 'primary'"
                  :readonly="!userCanUpdate"
                  :base-color="userCanUpdate ? 'warning' : 'primary'"
                  inset
                  control-variant="stacked"
                  :step="0.5"
                  :precision="2"
                  label="Cooling Signal %"
                  :min="0"
                  :max="100"
                  v-model="localSettings.coolingSignal"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <!--Damper Control Card-->
        <v-col :cols="height >= maxHeight ? 12 : 4">
          <v-card variant="outlined" class="mb-n5 px-2">
            <v-row>
              <v-col>
                <span
                  class="text-primary text-caption text-sm-subtitle-1 font-weight-bold float-left pa-2"
                >
                  Damper Control
                </span>
              </v-col>
            </v-row>

            <v-divider></v-divider>

            <v-row class="mt-1 px-1">
              <v-col cols="6">
                <v-number-input
                  class="rounded-2 text-primary"
                  :color="userCanUpdate ? 'warning' : 'primary'"
                  :readonly="!userCanUpdate"
                  :base-color="userCanUpdate ? 'warning' : 'primary'"
                  inset
                  control-variant="stacked"
                  :step="0.5"
                  :precision="2"
                  label="Desired Airflow (cfm)"
                  v-model="localSettings.desiredAirflow"
                  variant="outlined"
                  density="compact"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  class="text-primary"
                  label="Current Airflow (cfm)"
                  :model-value="VAVInfo.currentAirflow"
                  variant="outlined"
                  density="compact"
                  readonly
                />
              </v-col>
            </v-row>
            <v-row class="px-1 mt-n7">
              <v-col>
                <v-text-field
                  class="text-primary"
                  label="Need More Airflow %"
                  :model-value="VAVInfo.needMoreAirflow"
                  variant="outlined"
                  density="compact"
                  readonly
                />
              </v-col>
            </v-row>
            <v-row class="px-1 mt-n7">
              <v-col>
                <v-text-field
                  class="text-primary"
                  label="Open Estimated Damper Position %"
                  :model-value="VAVInfo.openEstimatedDamperPosition"
                  variant="outlined"
                  density="compact"
                  readonly
                />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mt-5">
        <v-col cols="6" align-self="center">
          <v-btn
            :disabled="isVAVSetLoading"
            data-testid="resetVAVButton"
            class="rounded-2"
            color="primary"
            variant="flat"
            block
            size="large"
            @click="isVAVDirty ? resetChangedSettings() : handleCancel()"
          >
            <div
              class="text-subtitle-2 text-white text-truncate font-weight-bold"
            >
              {{ isVAVDirty ? 'Reset' : 'Cancel' }}
            </div>
          </v-btn>
        </v-col>
        <v-col cols="6">
          <v-btn
            :loading="isVAVSetLoading"
            :disabled="!isVAVDirty || !userCanUpdate"
            data-testid="setVAVButton"
            class="rounded-2"
            :color="!isVAVDirty ? 'grey' : 'accentOrange'"
            variant="flat"
            block
            size="large"
            @click="handleSet"
          >
            <div
              class="text-subtitle-2 text-white text-truncate font-weight-bold"
            >
              Set
            </div>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script setup lang="ts">
import { ref, watch, computed, inject } from 'vue';

import { IVAV } from '@/Core.Service.Domain/Controls/Controls.Common/types/index.ts';
import API from '@/Core.Service.Domain/Controls/DigitalTwin/MGMOB/API';

import VAVCardAlert from '@/Core.Service.Domain/Controls/DigitalTwin/MGMOB/Components/VAVCardAlert.vue';

import eventBus from '@/Core.Service.Domain/Shared/eventBus.js';

import { ToastService } from '@/plugins/toasts';

import { useDisplay } from 'vuetify';

import { useAbility } from '@/plugins/useAbility.ts';

import { capitalizeFirstLetter } from '@/Core.Patterns/Adapter/index.ts';
import { splitCamelCase } from '@/Core.Patterns/Factory/index.ts';

const { height } = useDisplay();

const maxHeight = ref(1260);
const cardWidth = computed(() => {
  if (height.value < maxHeight.value) {
    return '1200px';
  } else {
    return '360px';
  }
});

const props = defineProps({
  VAVInfo: {
    type: Object as () => IVAV,
    required: true,
  },
  borderColor: {
    type: String,
    default: '#378CDA',
  },
});

const isVAVSetLoading = ref(false);
const localSettings = ref<
  Pick<
    IVAV,
    | 'occupiedSetpoint'
    | 'maxOccupiedSetpoint'
    | 'minOccupiedSetpoint'
    | 'heatingSignal'
    | 'coolingSignal'
    | 'desiredAirflow'
  >
>({
  occupiedSetpoint: 0,
  maxOccupiedSetpoint: 0,
  minOccupiedSetpoint: 0,
  heatingSignal: 0,
  coolingSignal: 0,
  desiredAirflow: 0,
});

const isDirty = ref({
  occupiedSetpoint: false,
  heatingSignal: false,
  coolingSignal: false,
  desiredAirflow: false,
  maxOccupiedSetpoint: false,
  minOccupiedSetpoint: false,
});

const isVAVDirty = computed(() =>
  Object.values(isDirty.value).some((value) => value)
);

const ability = useAbility();
const userCanUpdate = computed(() => {
  return ability.can('update', 'controls');
});

const toast = inject<ToastService>('toast')!;
const handleSet = async () => {
  if (!isVAVDirty.value || !userCanUpdate.value) return;
  type PermissibleKeys =
    | 'occupiedSetpoint'
    | 'maxOccupiedSetpoint'
    | 'minOccupiedSetpoint'
    | 'heatingSignal'
    | 'coolingSignal'
    | 'desiredAirflow';

  const payload = Object.entries(isDirty.value).reduce(
    (acc, [key, isChanged]) => {
      if (isChanged) {
        acc[key as PermissibleKeys] =
          localSettings.value[key as PermissibleKeys];
      }
      return acc;
    },
    {} as Partial<IVAV>
  );

  isVAVSetLoading.value = true;

  try {
    await API.setVAVSetpoint(props.VAVInfo.deviceId, payload);
    resetIsDirty();

    toast.show({
      title: 'Success',
      message: `VAV settings have been updated successfully.<br /> Values can take up to 2 minutes to reflect.`,
      status: 'success',
    });
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isVAVSetLoading.value = false;
  }
};

const resetIsDirty = () => {
  isDirty.value = {
    occupiedSetpoint: false,
    heatingSignal: false,
    coolingSignal: false,
    desiredAirflow: false,
    maxOccupiedSetpoint: false,
    minOccupiedSetpoint: false,
  };
};

const getVAVNewSettings = () => {
  const dirtySetting: {
    name: string;
    currentSetting: string;
    newSetting: string;
  }[] = [];

  Object.entries(isDirty.value).forEach(([key, value]) => {
    if (value) {
      const typedKey = key as keyof IVAV;
      dirtySetting.push({
        name: splitCamelCase(capitalizeFirstLetter(`${key}`)),
        currentSetting: `${props.VAVInfo[typedKey]}`,
        newSetting: `${
          localSettings.value[
            typedKey as
              | 'occupiedSetpoint'
              | 'maxOccupiedSetpoint'
              | 'minOccupiedSetpoint'
              | 'heatingSignal'
              | 'coolingSignal'
              | 'desiredAirflow'
          ]
        }`,
      });
    }
  });

  return dirtySetting;
};

const resetChangedSettings = () => {
  if (!isVAVDirty.value) return;
  localSettings.value = {
    occupiedSetpoint: props.VAVInfo.occupiedSetpoint,
    maxOccupiedSetpoint: props.VAVInfo.maxOccupiedSetpoint,
    minOccupiedSetpoint: props.VAVInfo.minOccupiedSetpoint,
    heatingSignal: props.VAVInfo.heatingSignal,
    coolingSignal: props.VAVInfo.coolingSignal,
    desiredAirflow: props.VAVInfo.desiredAirflow,
  };
  resetIsDirty();
};

const handleCancel = () => {
  resetChangedSettings();
  resetIsDirty();
  eventBus.$emit('update:selectedVAVId', props.VAVInfo.deviceId);
};

watch(
  () => [
    props.VAVInfo.occupiedSetpoint,
    props.VAVInfo.maxOccupiedSetpoint,
    props.VAVInfo.minOccupiedSetpoint,
    props.VAVInfo.heatingSignal,
    props.VAVInfo.coolingSignal,
    props.VAVInfo.desiredAirflow,
  ],
  () => {
    localSettings.value = {
      occupiedSetpoint: props.VAVInfo.occupiedSetpoint,
      maxOccupiedSetpoint: props.VAVInfo.maxOccupiedSetpoint,
      minOccupiedSetpoint: props.VAVInfo.minOccupiedSetpoint,
      heatingSignal: props.VAVInfo.heatingSignal,
      coolingSignal: props.VAVInfo.coolingSignal,
      desiredAirflow: props.VAVInfo.desiredAirflow,
    };
  },
  { immediate: true }
);

watch(
  () => [
    localSettings.value.occupiedSetpoint,
    localSettings.value.maxOccupiedSetpoint,
    localSettings.value.minOccupiedSetpoint,
    localSettings.value.heatingSignal,
    localSettings.value.coolingSignal,
    localSettings.value.desiredAirflow,
  ],
  ([
    occupiedSetpoint,
    maxOccupiedSetpoint,
    minOccupiedSetpoint,
    heatingSignal,
    coolingSignal,
    desiredAirflow,
  ]) => {
    isDirty.value.occupiedSetpoint =
      Number(occupiedSetpoint) !== props.VAVInfo.occupiedSetpoint;
    isDirty.value.maxOccupiedSetpoint =
      Number(maxOccupiedSetpoint) !== props.VAVInfo.maxOccupiedSetpoint;
    isDirty.value.minOccupiedSetpoint =
      Number(minOccupiedSetpoint) !== props.VAVInfo.minOccupiedSetpoint;
    isDirty.value.heatingSignal =
      Number(heatingSignal) !== props.VAVInfo.heatingSignal;
    isDirty.value.coolingSignal =
      Number(coolingSignal) !== props.VAVInfo.coolingSignal;
    isDirty.value.desiredAirflow =
      Number(desiredAirflow) !== props.VAVInfo.desiredAirflow;
  }
);
</script>

<style lang="scss">
.vav-control-tooltip {
  background: none !important;
}
</style>

<style lang="scss" scoped>
.v-text-field :deep(.v-text-field__slot) {
  input,
  .v-select__selection {
    color: $primary !important;
  }
}

.v-text-field :deep(.v-text-field__suffix) {
  padding-left: 0 !important;
  padding-right: 10px !important;
}

.v-alert :deep(.v-alert__border) {
  opacity: 1 !important;
}

.vav-control-dirty-icon {
  cursor: pointer;
}

:deep(input[readonly]) {
  color: grey !important;
  cursor: not-allowed;
}
</style>
